import { useEffect, useState, type KeyboardEvent } from 'react';
import { Save } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import type { Subject, SubjectCategory } from '@/types/subject';
import { subjectsApi } from '../api/subjects.api';
import { SUBJECT_CATEGORY_HINT, SUBJECT_CATEGORY_OPTIONS } from '../constants';
import { EMPTY_SUBJECT, subjectSchema, type SubjectFormValues } from '../schemas/subject.schema';

export interface SubjectFormModalProps {
  /** The subject being corrected, or null to add one. */
  subject: Subject | null;
  /** The category a new subject starts in — the tab it was added from. */
  defaultCategory?: SubjectCategory;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (subject: Subject) => void;
}

type FieldErrors = Partial<Record<keyof SubjectFormValues, string>>;

/**
 * Adding a subject, or renaming it or moving it to another category.
 *
 * A name that is the same subject as one already on the list — "Maths" when
 * "Mathematics" is there, in any category — is refused by the server, which
 * names the subject it clashes with. That message lands on the name field, so
 * the office reads what to use instead rather than a bare "already exists".
 */
export function SubjectFormModal({
  subject,
  defaultCategory,
  isOpen,
  onClose,
  onSaved,
}: SubjectFormModalProps) {
  const [values, setValues] = useState<SubjectFormValues>(EMPTY_SUBJECT);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Refilled each time it opens, so the last subject never shows under the next.
  useEffect(() => {
    if (!isOpen) return;
    setValues(
      subject
        ? { name: subject.name, category: subject.category }
        : { ...EMPTY_SUBJECT, category: defaultCategory ?? EMPTY_SUBJECT.category },
    );
    setFieldErrors({});
    setFormError(null);
  }, [subject, defaultCategory, isOpen]);

  function set<K extends keyof SubjectFormValues>(key: K, value: SubjectFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
  }

  async function handleSubmit() {
    if (isSaving) return;
    const parsed = subjectSchema.safeParse(values);

    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof SubjectFormValues | undefined;
        if (field && !errors[field]) errors[field] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);

    try {
      const saved = subject
        ? await subjectsApi.update(subject.uuid, parsed.data)
        : await subjectsApi.create(parsed.data);
      onSaved(saved);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === 'SUBJECT_NAME_TAKEN') {
        setFieldErrors({ name: caught.fieldMap.name ?? caught.message });
        return;
      }
      setFormError(
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  function submitOnEnter(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    void handleSubmit();
  }

  const isMovingClasses =
    subject !== null && values.category !== subject.category && subject.usedBy.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={subject ? `Edit ${capitalizeWords(subject.name)}` : 'Add a subject'}
      description="Each subject is one category everywhere. Classes pick their core subjects, electives and languages from this list."
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            isLoading={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {subject ? 'Save changes' : 'Add subject'}
          </Button>
        </>
      }
    >
      {/* Not a <form>: this opens from inside the class setup form too, and
          `Modal` renders in place. A nested form's submit does not bubble, so
          React never saw it and the browser submitted the page for real —
          reloading class setup back to its first step. Enter is kept by hand. */}
      <div onKeyDown={submitOnEnter} className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save this subject">
            {formError}
          </Alert>
        )}

        <Input
          label="Subject name"
          placeholder="e.g. Mathematics"
          autoComplete="off"
          value={values.name}
          error={fieldErrors.name}
          hint={
            subject && subject.usedBy.length > 0
              ? 'Renaming it renames it on every class, timetable and report card that carries it.'
              : undefined
          }
          onChange={(event) => set('name', event.target.value)}
        />

        <Select
          label="Category"
          options={SUBJECT_CATEGORY_OPTIONS}
          value={values.category}
          error={fieldErrors.category}
          hint={SUBJECT_CATEGORY_HINT[values.category]}
          onChange={(event) => set('category', event.target.value as SubjectCategory)}
        />

        {isMovingClasses && subject && (
          <Alert tone="warning" title="This moves it in every class">
            {subject.usedBy.length === 1
              ? `${subject.usedBy[0]} lists ${capitalizeWords(subject.name)}; it will be listed under the new category there too.`
              : `${subject.usedBy.length} classes list ${capitalizeWords(subject.name)}; it will be listed under the new category in all of them.`}
          </Alert>
        )}
      </div>
    </Modal>
  );
}
